/* FIDNO funnel: arrival estimate, sticky Add to cart and "Complete the kit".
   Loaded with defer wherever a funnel snippet renders; every element guards its
   own definition so repeated tags are harmless. */

(() => {
  /* ---------- <fidno-eta>: business-day arrival window ---------- */
  const addBusinessDays = (from, days) => {
    const date = new Date(from);
    // an order placed on a weekend starts processing on Monday
    while (date.getDay() === 0 || date.getDay() === 6) date.setDate(date.getDate() + 1);
    let left = days;
    while (left > 0) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) left -= 1;
    }
    return date;
  };

  if (!customElements.get('fidno-eta')) {
    customElements.define(
      'fidno-eta',
      class FidnoEta extends HTMLElement {
        connectedCallback() {
          const min = parseInt(this.dataset.min, 10);
          const max = parseInt(this.dataset.max, 10);
          if (Number.isNaN(min) || Number.isNaN(max)) return;
          const today = new Date();
          const first = addBusinessDays(today, min);
          const last = addBusinessDays(today, max);
          const day = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
          this.textContent = `Arrives ${day.format(first)} – ${day.format(last)}`;
        }
      }
    );
  }

  /* ---------- <fidno-sticky-atc>: phones only, mirrors the real button ---------- */
  if (!customElements.get('fidno-sticky-atc')) {
    customElements.define(
      'fidno-sticky-atc',
      class FidnoStickyAtc extends HTMLElement {
        connectedCallback() {
          // fixed positioning must not inherit a transformed ancestor's containing block
          if (this.parentElement !== document.body) {
            document.querySelectorAll(`fidno-sticky-atc[data-section="${this.dataset.section}"]`).forEach((el) => {
              if (el !== this && el.parentElement === document.body) el.remove();
            });
            document.body.appendChild(this);
            return;
          }
          this.main = document.getElementById(this.dataset.button);
          if (!this.main) return;
          this.button = this.querySelector('[data-sticky-button]');
          this.label = this.button.querySelector('span');
          this.priceEl = this.querySelector('[data-sticky-price]');
          this.variantEl = this.querySelector('[data-sticky-variant]');
          this.hidden = false;

          this.button.addEventListener('click', () => {
            if (this.main.disabled || this.main.getAttribute('aria-disabled') === 'true') {
              this.main.closest('.product__info-container, .product')?.querySelector('variant-selects, .product-form__input')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              return;
            }
            this.main.click();
          });

          this.sync = this.sync.bind(this);
          this.mirror = new MutationObserver(this.sync);
          this.mirror.observe(this.main, { attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'], childList: true, subtree: true, characterData: true });
          const price = document.getElementById(this.dataset.price);
          if (price) this.mirror.observe(price, { childList: true, subtree: true, characterData: true });

          if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
            this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
              if (event.data?.sectionId && event.data.sectionId !== this.dataset.section) return;
              const variant = event.data?.variant;
              if (this.variantEl) this.variantEl.textContent = variant ? variant.title : '';
              requestAnimationFrame(this.sync);
            });
          }

          this.observer = new IntersectionObserver(
            ([entry]) => {
              // show only once the button has scrolled up and out, not before reaching it
              const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              this.classList.toggle('is-visible', past);
              this.setAttribute('aria-hidden', String(!past));
              this.button.tabIndex = past ? 0 : -1;
            },
            { threshold: 0 }
          );
          this.observer.observe(this.main);
          this.setAttribute('aria-hidden', 'true');
          this.button.tabIndex = -1;
          this.sync();
        }

        disconnectedCallback() {
          this.observer?.disconnect();
          this.mirror?.disconnect();
          this.unsubscribe?.();
        }

        sync() {
          const text = this.main.querySelector('span')?.textContent.trim();
          if (text) this.label.textContent = text;
          const disabled = this.main.disabled;
          this.button.classList.toggle('is-unavailable', disabled);
          this.button.setAttribute('aria-disabled', String(disabled));
          this.button.classList.toggle('loading', this.main.classList.contains('loading'));
          const price = document.getElementById(this.dataset.price)?.querySelector('.price-item--sale, .price-item--regular');
          const shown = price && price.offsetParent !== null ? price : document.getElementById(this.dataset.price)?.querySelector('.price-item');
          if (shown && this.priceEl) this.priceEl.textContent = shown.textContent.trim();
        }
      }
    );
  }

  /* ---------- <fidno-cart-recs>: "Complete the kit" ---------- */
  const recsCache = new Map();

  const sectionsFor = () => {
    const list = [];
    if (document.querySelector('cart-drawer')) list.push('cart-drawer');
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    if (items?.dataset.id) list.push(items.dataset.id);
    if (footer?.dataset.id) list.push(footer.dataset.id);
    list.push('cart-icon-bubble');
    return list;
  };

  const inner = (html, selector) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector(selector)?.innerHTML;
  };

  const applySections = (sections, variantId, fromDrawer) => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && sections['cart-drawer']) {
      if (fromDrawer && typeof drawer.renderContents === 'function') {
        drawer.renderContents({ id: variantId, sections });
      } else {
        const html = inner(sections['cart-drawer'], '#CartDrawer');
        const target = document.getElementById('CartDrawer');
        if (html && target) {
          target.innerHTML = html;
          drawer.classList.remove('is-empty');
        }
      }
    }
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    [items, footer].forEach((el) => {
      if (!el?.dataset.id || !sections[el.dataset.id]) return;
      const html = inner(sections[el.dataset.id], '.js-contents');
      const target = el.querySelector('.js-contents');
      if (html != null && target) target.innerHTML = html;
      el.classList.remove('is-empty');
    });
    if (items) document.querySelector('.fidno-cart-grid')?.classList.remove('is-empty');
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble'] && !fromDrawer) {
      const html = inner(sections['cart-icon-bubble'], '.shopify-section');
      if (html != null) bubble.innerHTML = html;
    }
  };

  if (!customElements.get('fidno-cart-recs')) {
    customElements.define(
      'fidno-cart-recs',
      class FidnoCartRecs extends HTMLElement {
        connectedCallback() {
          this.list = this.querySelector('.fidno-recs__list');
          this.status = this.querySelector('.fidno-recs__status');
          this.exclude = new Set((this.dataset.exclude || '').split(',').filter(Boolean));
          this.limit = parseInt(this.dataset.limit, 10) || 3;
          this.addEventListener('click', (event) => {
            const button = event.target.closest('[data-variant-id]');
            if (button && !button.disabled) this.add(button);
          });
          this.load();
        }

        async load() {
          const id = this.dataset.productId;
          if (!id) return;
          let html = recsCache.get(id);
          if (!html) {
            try {
              const url = `${this.dataset.url}?product_id=${id}&limit=10&section_id=fidno-cart-recs&intent=related`;
              const response = await fetch(url);
              if (!response.ok) return;
              html = await response.text();
              recsCache.set(id, html);
            } catch (e) {
              return;
            }
          }
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const rows = [...doc.querySelectorAll('.fidno-recs__item')]
            .filter((row) => !this.exclude.has(row.dataset.productId))
            .slice(0, this.limit);
          if (!rows.length) return;
          this.list.replaceChildren(...rows.map((row) => document.importNode(row, true)));
          this.hidden = false;
          requestAnimationFrame(() => this.classList.add('is-ready'));
        }

        async add(button) {
          if (this.busy) return;
          this.busy = true;
          const variantId = button.dataset.variantId;
          const fromDrawer = Boolean(this.closest('cart-drawer'));
          button.classList.add('is-adding');
          button.setAttribute('aria-busy', 'true');
          this.status.textContent = '';
          try {
            const response = await fetch(`${routes.cart_add_url}.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                items: [{ id: Number(variantId), quantity: 1 }],
                sections: sectionsFor(),
                sections_url: window.location.pathname,
              }),
            });
            const data = await response.json();
            if (!response.ok || data.status) {
              this.status.textContent = data.description || data.message || 'That size could not be added. Try another.';
              return;
            }
            if (window.PUB_SUB_EVENTS && typeof publish === 'function') {
              // tell other listeners (sticky bars, analytics) without re-fetching the cart twice
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', productVariantId: variantId, cartData: data });
            }
            applySections(data.sections || {}, variantId, fromDrawer);
          } catch (e) {
            this.status.textContent = 'Connection lost. Nothing was added; try again.';
          } finally {
            this.busy = false;
            button.classList.remove('is-adding');
            button.removeAttribute('aria-busy');
          }
        }
      }
    );
  }
})();
